import React, { useState, useEffect, useRef, useCallback } from 'react';

const getScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#2563eb';
    if (score >= 40) return '#eab308';
    if (score >= 20) return '#f97316';
    return '#ef4444';
};

/**
 * Semi-circle gauge showing a 0–100 percentile score.
 * The tooltip is rendered with position: fixed and positioned via JS so it is
 * never clipped by the card's overflow: hidden.
 */
const MetricGauge = ({ score = 0, summaryGauge = false, label }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPos, setTooltipPos] = useState(null);
    const containerRef = useRef(null);
    const tooltipRef = useRef(null);

    const safeScore = Math.max(0, Math.min(100, Number(score) || 0));
    const rounded = Math.round(safeScore);
    const color = getScoreColor(safeScore);

    const radius = 20;
    const strokeWidth = 5;
    const circumference = radius * Math.PI;
    const progressLength = (safeScore / 100) * circumference;

    const positionTooltip = useCallback(() => {
        const gauge = containerRef.current;
        const tip = tooltipRef.current;
        if (!gauge || !tip) return;

        const gaugeRect = gauge.getBoundingClientRect();
        const tipRect = tip.getBoundingClientRect();
        const margin = 8;
        const vw = window.innerWidth;

        let left = gaugeRect.left + gaugeRect.width / 2 - tipRect.width / 2;
        left = Math.max(margin, Math.min(vw - tipRect.width - margin, left));

        let top = gaugeRect.top - tipRect.height - margin;
        let placement = 'top';
        if (top < margin) {
            top = gaugeRect.bottom + margin;
            placement = 'bottom';
        }

        const arrowLeft = gaugeRect.left + gaugeRect.width / 2 - left;
        setTooltipPos({ left, top, arrowLeft: Math.max(12, Math.min(tipRect.width - 12, arrowLeft)), placement });
    }, []);

    useEffect(() => {
        if (!showTooltip) return undefined;
        positionTooltip();

        const close = () => setShowTooltip(false);
        const onDocClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) close();
        };
        window.addEventListener('scroll', close, { passive: true, capture: true });
        window.addEventListener('resize', close);
        document.addEventListener('pointerdown', onDocClick);
        return () => {
            window.removeEventListener('scroll', close, { capture: true });
            window.removeEventListener('resize', close);
            document.removeEventListener('pointerdown', onDocClick);
        };
    }, [showTooltip, positionTooltip]);

    const tooltipText = summaryGauge
        ? `${label ? `${label}: ` : ''}besser als ${rounded}% der Vergleichsunternehmen`
        : `Dieser Wert ist besser als bei ${rounded}% der berücksichtigten Unternehmen`;

    return (
        <div
            ref={containerRef}
            className={`gauge-container ${summaryGauge ? 'summary-gauge-container' : ''}`}
            role="img"
            aria-label={`${label ? `${label}: ` : ''}Score ${rounded} von 100`}
            tabIndex={0}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onFocus={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
            onClick={(e) => {
                // Gauges live inside clickable cards: never toggle the card.
                e.stopPropagation();
                e.preventDefault();
                // Taps fire mouseenter first; always show and let the
                // outside-tap/scroll handlers close it.
                setShowTooltip(true);
            }}
        >
            <svg
                className="gauge-svg"
                width={radius * 2}
                height={radius + strokeWidth}
                viewBox={`0 0 ${radius * 2} ${radius + strokeWidth}`}
                aria-hidden="true"
                focusable="false"
            >
                <path
                    d={`M ${strokeWidth / 2},${radius + strokeWidth / 2} A ${radius - strokeWidth / 2},${radius - strokeWidth / 2} 0 0 1 ${2 * radius - strokeWidth / 2},${radius + strokeWidth / 2}`}
                    fill="none"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                />
                <path
                    d={`M ${strokeWidth / 2},${radius + strokeWidth / 2} A ${radius - strokeWidth / 2},${radius - strokeWidth / 2} 0 0 1 ${2 * radius - strokeWidth / 2},${radius + strokeWidth / 2}`}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={`${progressLength} ${circumference}`}
                />
                <text
                    x={radius}
                    y={radius + 1}
                    fill={color}
                    fontSize="9.5"
                    textAnchor="middle"
                >
                    {rounded}%
                </text>
            </svg>

            {showTooltip && (
                <div
                    ref={tooltipRef}
                    className={`metric-tooltip ${summaryGauge ? 'summary-tooltip' : ''} ${tooltipPos?.placement === 'bottom' ? 'metric-tooltip--below' : ''}`}
                    role="tooltip"
                    style={tooltipPos ? { left: tooltipPos.left, top: tooltipPos.top, visibility: 'visible' } : { visibility: 'hidden', left: 0, top: 0 }}
                >
                    {tooltipText}
                    <div
                        className="metric-tooltip-arrow"
                        style={tooltipPos ? { left: tooltipPos.arrowLeft } : undefined}
                    ></div>
                </div>
            )}
        </div>
    );
};

export default MetricGauge;
